import {
  Button,
  Checkbox,
  Colors,
  Icon,
  InputGroup,
  Popover,
  TextArea,
  Tooltip,
} from '@blueprintjs/core';
import BOSInput from 'Components/Generic/BOSInput';
import ResourceMultiselect from 'Components/Generic/BOSMultiselect/ResourceMultiselect';
import { StatusSelectButton } from 'Components/Generic/BOSSelect/StatusSelect';
import {
  showSaveSuccessToast,
  showSuccessToast,
} from 'Components/Generic/BOSToasterUtil';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import DuplicateButton from 'Components/Generic/Buttons/DuplicateButton';
import DetailTable from 'Components/Generic/DetailPanel/DetailTable';
import BOSNoWrapText from 'Components/Generic/Misc/BOSNoWrapText';
import UserNameWithDrawerButton from 'Components/SiteDrawerContainer/UserNameWithDrawerButton';
import AppData from 'Data/AppData';
import { doesOrgLinkResourcesToLineItems } from 'Data/context/Organizations';
import { useShippingContext } from 'Data/context/Shipping';
import { GetLineItemDataBus, LineItemData } from 'Data/LineItemData';
import RequestData from 'Data/RequestData';
import { TransactionData } from 'Data/TransactionData';
import DataManager from 'Lib/DataManager';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import { getLineItemStatus, LINE_ITEM_STATUS } from 'Pages/Transactions/Enum';
import PropTypes, { oneOfType } from 'prop-types';
import { Fragment, useEffect, useRef, useState } from 'react';
import {
  areEquivalent,
  formatMoney,
  getClickableLink,
  getGrandTotalForTransaction,
} from 'Util';
import { formatDate } from 'Util/dateUtil';
import { getTagValue, setTagValue } from 'Util/tagUtil';
import AddNewButton from '../Buttons/AddNewButton';
import ButtonFooter from '../Buttons/ButtonFooter';
import SaveButton from '../Buttons/SaveButton';
import UndoButton from '../Buttons/UndoButton';
import NoContentBox from '../Misc/NoContentBox';
import styles from './LineItemsList.module.css';

let DM = new DataManager();
const tableColumnWidths = {
  cat: 120,
  description: 400,
  unit: 58,
  qty: 60,
  price: 112,
  total: 80,
  resources: 200,
  link: '25%',
  radioactive: 60,
  status: 100,
  received: 100,
  del: 20,
};

const LineItemList = ({
  editable = false,
  request,
  transaction,
  onUpdateLineItems = () => {},
  isNew = false,
  compact = false,
  setEditState = () => {},
}) => {
  const LineItemDataRef = useRef(GetLineItemDataBus());
  let CurrentLineItemData = LineItemDataRef.current;

  const shippingContext = useShippingContext();

  const [orgId, setOrgId] = useState();
  const [linkResourcesToLineItems, setLinkResourcesToLineItems] =
    useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const [lineItems, setLineItems, lineItemsRef] = useStateWithRef([]);
  const [deletedLineItems, setDeletedLineItems] = useState([]);
  const [linesHaveBeenUpdated, setLinesHaveBeenUpdated] = useState(false);
  const [grandTotal, setGrandTotal] = useState(0);
  const requestRef = useRef();
  const transRef = useRef();
  let shippingItemRef = shippingContext.shippingItemRef;
  let shippingItem = shippingContext.shippingItem;

  useEffect(() => {
    DM.add(
      CurrentLineItemData.registerListener(
        'BLANK_RECORD_LOADED',
        onBlankLineItemLoaded,
      ),
      CurrentLineItemData,
    );
    DM.add(
      CurrentLineItemData.registerListener('LIST_LOADED', onLineItemsLoaded),
      CurrentLineItemData,
    );
    DM.add(
      CurrentLineItemData.registerListener('RECORDS_LOADED', onLineItemsLoaded),
      CurrentLineItemData,
    );
    DM.add(
      CurrentLineItemData.registerListener(
        'LINE_ITEMS_UPDATED',
        onLineItemsUpdated,
      ),
      CurrentLineItemData,
    );
    DM.add(
      CurrentLineItemData.registerListener(
        'RECORDS_UPDATE_ERROR',
        onLineItemsUpdateError,
      ),
      CurrentLineItemData,
    );
    DM.add(
      LineItemData.registerListener(
        'LINE_ITEMS_MARKED_RECEIVED',
        onLineItemsMarkedReceived,
      ),
      LineItemData,
    );
    DM.add(
      LineItemData.registerListener(
        'LINE_ITEMS_MARKED_AS_PURCHASED',
        onLineItemsMarkedAsPurchased,
      ),
      LineItemData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    setDisableSave(false);
    setEditState(undefined);
  }, []);

  useEffect(() => {
    setDisableSave(false);
    setEditState(undefined);
  }, [editable]);

  useEffect(() => {
    let parentOrgId =
      transaction?.parent_organization_id || request?.parent_organization_id;
    parentOrgId && setOrgId(parentOrgId);
  }, [request?.parent_organization_id, transaction?.parent_organization_id]);

  useEffect(() => {
    setLinkResourcesToLineItems(doesOrgLinkResourcesToLineItems(orgId));
  }, [orgId]);

  useEffect(() => {
    transRef.current = transaction;
    transaction && fetchLineItems();
  }, [transaction]);

  useEffect(() => {
    requestRef.current = request;
    request && !transaction && fetchLineItems();
  }, [request]);

  useEffect(() => {
    if (
      transaction?.id &&
      shippingItem?.id &&
      shippingItem?.parent_transaction_id !== transaction.id
    ) {
      shippingContext.updateShipping('parent_transaction_id', transaction.id);
    }
  }, [transaction, shippingItem]);

  useEffect(() => {
    setGrandTotal(getGrandTotalForTransaction(lineItems, shippingItem?.amount));
  }, [lineItems, shippingItem?.amount]);

  const fetchLineItems = () => {
    let where = {};
    if (request?.id || transaction?.id) {
      if (transaction?.id) {
        where['*assigned_transaction_id'] = transaction.id;
      } else if (request?.id) {
        where['*source_request_id'] = request.id;
        where['*assigned_transaction_id'] = '$NULL$';
      }
      CurrentLineItemData.getRecordList({
        where,
        _include: 'resources',
        _sort: 'id',
      });
    } else {
      if (!transaction) {
        setLineItems(request?.line_items || []);
      } else {
        setLineItems(transaction?.line_items || []);
      }
    }
  };

  const onLineItemsLoaded = (payload) => {
    if (payload?.records) {
      setDisableSave(false);
      setLinesHaveBeenUpdated(false);
      setLineItems(payload.records);
      setDeletedLineItems([]);
      if (!payload.records.length && isNew) {
        CurrentLineItemData.getBlank();
      }
    }
  };

  const onBlankLineItemLoaded = (newLineItem) => {
    setLinesHaveBeenUpdated(true);
    let updatedLineItem = { ...newLineItem };
    if (requestRef.current?.id) {
      updatedLineItem.source_request_id = requestRef.current.id;
    }
    if (transRef.current?.id) {
      updatedLineItem.assigned_transaction_id = transRef.current.id;
      updatedLineItem.source_request_id =
        transRef.current.originating_request_id;
    }
    if (shippingItemRef?.current?.id) {
      updatedLineItem.shipping_id = shippingItemRef.current.id;
    }
    setLineItems((prevLineItems) => [...prevLineItems, updatedLineItem]);
  };

  const duplicateLineItem = (newLineItem) => {
    setLinesHaveBeenUpdated(true);
    let updatedLineItem = LineItemData.getSimpleLineItem(newLineItem, true);
    setLineItems((prevLineItems) => [...prevLineItems, updatedLineItem]);
  };

  const updateItemValue = (
    itemToUpdate,
    updateField,
    updatedValue,
    saveItemImmediately,
  ) => {
    const updatedArray = lineItems.map((item) => {
      if (item === itemToUpdate) {
        let originalItem = { ...item };
        let updatedItem = { ...item, [updateField]: updatedValue };
        if (!areEquivalent(originalItem, updatedItem)) {
          setLinesHaveBeenUpdated(true);
          saveItemImmediately && LineItemData.updateRecord(updatedItem);
        }
        return updatedItem;
      }
      return item;
    });
    setLineItems(updatedArray);
  };

  const saveClicked = () => {
    setDisableSave(true);

    // Update shipping speed on request if this is a line item list for a request
    request &&
      !transaction &&
      RequestData.updateShippingSpeed(request, shippingItem.shipping_speed);

    // Update transaction total
    transaction &&
      TransactionData.updatePurchaseTotal(
        transaction,
        lineItems,
        shippingItem?.amount,
      );

    // For existing shipping/line item, save line items.
    // (For a new shipping item, we get the saved shipping, update the shipping id
    // on the line items, and then save them.)
    if (shippingItem?.id) {
      shippingContext.saveShipping();
      if (linesHaveBeenUpdated) {
        saveLineItems();
      } else {
        setEditState(false);
        setDisableSave(false);
      }
    } else {
      shippingContext.saveShipping().then((returnedShipping) => {
        CurrentLineItemData.updateOrCreateLineItems(
          lineItems.map((item) => {
            return { ...item, shipping_id: returnedShipping.id };
          }),
          'LINE_ITEMS_UPDATED',
          linkResourcesToLineItems,
        );

        deleteLineItems();
      });
    }
  };

  const saveLineItems = () => {
    CurrentLineItemData.updateOrCreateLineItems(
      lineItems,
      'LINE_ITEMS_UPDATED',
      linkResourcesToLineItems,
    );
    deleteLineItems();
  };

  const onMarkItemForDelete = (lineItemToDelete) => {
    setLinesHaveBeenUpdated(true);
    setLineItems((prevLineItems) =>
      prevLineItems.filter((item) => item !== lineItemToDelete),
    );
    if (lineItemToDelete.id) {
      setDeletedLineItems((prevLineItems) => [
        ...prevLineItems,
        lineItemToDelete,
      ]);
    }
  };

  const deleteLineItems = () => {
    if (deletedLineItems.length > 0) {
      CurrentLineItemData.deleteRecords(
        deletedLineItems.map((lineItem) => lineItem.id),
      );
    }
  };

  const onLineItemsUpdated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Line Items Saved');
      setEditState(false);
      onUpdateLineItems();
      onLineItemsLoaded(payload);
    }
  };

  const onLineItemsUpdateError = () => {
    setDisableSave(false);
  };

  const undoChanges = () => {
    fetchLineItems();
  };

  const onLineItemsMarkedAsPurchased = (payload) => {
    payload && showSuccessToast('Line items marked as purchased.');
  };

  const markLineItemReceived = (lineItem) => {
    LineItemData.markLinesReceived(lineItem);
  };

  const markAllLineItemsReceived = () => {
    LineItemData.markLinesReceived(lineItems);
  };

  const onLineItemsMarkedReceived = (updatedLines) => {
    if (
      !updatedLines ||
      !updatedLines
        .map((item) => item.id)
        .every((lineItemId) =>
          lineItemsRef.current.find((li) => li.id === lineItemId),
        )
    ) {
      return;
    }
    showSuccessToast(
      `Line item${updatedLines?.length > 1 ? 's' : ''} marked received`,
    );
    let newLines = lineItemsRef.current.map((line) => {
      let updatedLine = updatedLines.find((li) => li.id === line.id);
      return { ...line, ...(updatedLine || {}) };
    });
    onUpdateLineItems();
    onLineItemsLoaded({ records: newLines });
  };

  const getReceivedContent = (lineItem) => {
    let receivedInfo = [
      {
        label: 'Received By',
        value: <UserNameWithDrawerButton userId={lineItem.received_by} />,
      },
      { label: 'Received', value: formatDate(lineItem.date_received) },
      { label: 'Quantity Received', value: lineItem.quantity_received },
    ];
    return (
      <div className={styles.received_info_box}>
        <DetailTable data={receivedInfo}></DetailTable>
      </div>
    );
  };

  let showDummyCheckboxes =
    !editable &&
    (requestRef?.current?.processing_user_id === AppData.userID() ||
      transRef?.current?.request?.processing_user_id === AppData.userID() ||
      transRef?.current?.purchased_by === AppData.userID()) &&
    lineItems.some(
      (lineItem) => lineItem.status !== LINE_ITEM_STATUS.RECEIVED.key,
    );

  const canLineBeMarkedReceived = (lineItem) => {
    return (
      (lineItem.assigned_transaction_id || transRef?.current) &&
      [LINE_ITEM_STATUS.PURCHASED.key, LINE_ITEM_STATUS.RECEIVED.key].includes(
        lineItem.status,
      ) &&
      !lineItem.date_received
    );
  };

  const showRadioactiveCol =
    getTagValue(request, 'is_radioactive') ||
    getTagValue(transaction, 'is_radioactive') ||
    lineItems.some((li) => !!li.is_radioactive);

  let headers = (
    <tr>
      {showDummyCheckboxes && <td style={{ width: 10 }} />}

      <td
        style={{ width: tableColumnWidths.cat }}
        className={styles.table_header_cell}
        title='Catalog Number'>
        Cat. #
      </td>

      <td
        style={{ width: tableColumnWidths.description, textAlign: 'left' }}
        className={styles.table_header_cell}>
        Description
      </td>

      <td
        style={{ width: tableColumnWidths.unit }}
        className={styles.table_header_cell}>
        Unit
      </td>

      <td
        style={{ width: tableColumnWidths.qty }}
        className={styles.table_header_cell}>
        Qty
      </td>

      <td
        style={{ width: tableColumnWidths.price, textAlign: 'right' }}
        className={styles.table_header_cell}>
        Unit Price
      </td>

      <td
        style={{ width: tableColumnWidths.total, textAlign: 'right' }}
        className={styles.table_header_cell}>
        Total Price
      </td>

      {linkResourcesToLineItems && (
        <td
          style={{ width: tableColumnWidths.resources }}
          className={styles.table_header_cell}>
          Resources
        </td>
      )}

      {
        <td
          style={editable ? { width: tableColumnWidths.link } : {}}
          className={styles.table_header_cell}>
          Link
        </td>
      }

      {showRadioactiveCol && (
        <td
          style={{
            width: tableColumnWidths.radioactive,
            color: 'var(--site-warning-color)',
          }}
          className={styles.table_header_cell}>
          Radioactive
        </td>
      )}

      {!isNew && !editable && (
        <td
          style={{ width: tableColumnWidths.status }}
          className={styles.table_header_cell}>
          Status
        </td>
      )}

      {!isNew &&
        !editable &&
        lineItems.some((lineItem) => canLineBeMarkedReceived(lineItem)) && (
          <td
            style={{ width: tableColumnWidths.received }}
            className={styles.table_header_cell}></td>
        )}

      {editable && <td style={{ width: tableColumnWidths.del }}></td>}
    </tr>
  );

  const getResourceMultiselect = (lineItem, viewOnly) => {
    return (
      <ResourceMultiselect
        orgId={orgId}
        onSelectedResourcesUpdate={(list) =>
          updateItemValue(lineItem, 'resources', list)
        }
        initialSelectedItems={lineItem?.resources}
        viewOnly={viewOnly}
        maxWidth={tableColumnWidths.resources}
      />
    );
  };

  let totalColumns =
    10 +
    (editable ? 1 : 0) +
    (showDummyCheckboxes ? 1 : 0) +
    (linkResourcesToLineItems ? 1 : 0);

  let lineItemContent =
    !lineItems ||
    (lineItems.length === 0 && (
      <tr>
        <td colSpan={totalColumns}>
          <NoContentBox label='No Line Items' />
        </td>
      </tr>
    )) ||
    lineItems.map((lineItem, index) => {
      const getCommentBox = (padding, additionalNote) => (
        <div
          style={{
            padding,
            whiteSpace: 'pre-line',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}>
          {lineItem.comment}
          {additionalNote && (
            <div
              style={{
                marginLeft: 'auto',
                fontStyle: 'italic',
                fontSize: 'small',
              }}>
              {`(${additionalNote})`}
            </div>
          )}
        </div>
      );
      return (
        <Fragment key={index}>
          <tr key={index} className={index % 2 ? styles.light_background : ''}>
            {showDummyCheckboxes && (
              <td>
                <Checkbox
                  className={styles.dummy_checkboxes}
                  checked={getTagValue(lineItem, 'hasBeenProcessed') === 'true'}
                  onChange={(event) => {
                    updateItemValue(
                      lineItem,
                      'tags',
                      setTagValue(
                        lineItem,
                        'hasBeenProcessed',
                        event.target.checked,
                      ),
                      true,
                    );
                  }}
                />
              </td>
            )}

            <td style={{ textAlign: 'center' }}>
              {editable ? (
                <InputGroup
                  type='text'
                  value={lineItem.vendor_catalog_number || ''}
                  readOnly={!editable}
                  onChange={(event) =>
                    updateItemValue(
                      lineItem,
                      'vendor_catalog_number',
                      event.target.value,
                    )
                  }
                />
              ) : (
                <BOSNoWrapText>{lineItem.vendor_catalog_number}</BOSNoWrapText>
              )}
            </td>

            <td>
              {editable ? (
                <InputGroup
                  type='text'
                  value={lineItem.description || ''}
                  readOnly={!editable}
                  onChange={(event) =>
                    updateItemValue(lineItem, 'description', event.target.value)
                  }
                />
              ) : (
                <div className={styles.description_box}>
                  <BOSNoWrapText maxWidth={tableColumnWidths.description}>
                    {lineItem.description}
                  </BOSNoWrapText>
                  {lineItem.comment && compact && (
                    <Popover
                      className={styles.comment_popover}
                      minimal
                      position={'bottom-right'}
                      content={getCommentBox(8)}>
                      <Popover
                        minimal
                        content={getCommentBox(8)}
                        position={'bottom-right'}
                        hoverOpenDelay={200}
                        interactionKind={'hover'}>
                        <Icon
                          className={styles.comment_icon}
                          icon={'comment'}
                        />
                      </Popover>
                    </Popover>
                  )}
                </div>
              )}
            </td>

            <td style={{ textAlign: 'center' }}>
              {editable ? (
                <InputGroup
                  type='text'
                  style={{ textAlign: 'center' }}
                  value={lineItem.measure_unit || ''}
                  readOnly={!editable}
                  onChange={(event) =>
                    updateItemValue(
                      lineItem,
                      'measure_unit',
                      event.target.value,
                    )
                  }
                />
              ) : (
                <BOSNoWrapText maxWidth={tableColumnWidths.unit}>
                  {lineItem.measure_unit}
                </BOSNoWrapText>
              )}
            </td>

            <td style={{ textAlign: 'center' }}>
              {editable ? (
                <InputGroup
                  type='text'
                  style={{ textAlign: 'center' }}
                  value={lineItem.quantity || ''}
                  readOnly={!editable}
                  onChange={(event) =>
                    updateItemValue(lineItem, 'quantity', event.target.value)
                  }
                />
              ) : (
                <BOSNoWrapText maxWidth={tableColumnWidths.qty}>
                  {lineItem.quantity}
                </BOSNoWrapText>
              )}
            </td>

            <td>
              {editable ? (
                <BOSInput
                  onChange={(val) =>
                    updateItemValue(lineItem, 'unit_price', val)
                  }
                  isMoneyInput
                  value={lineItem?.unit_price}
                />
              ) : (
                <div
                  className={styles.money_cell}
                  style={{ display: 'flex', alignItems: 'baseline' }}>
                  <Icon
                    size={12}
                    icon='dollar'
                    style={{ marginRight: 'auto' }}
                  />
                  {formatMoney(+lineItem?.unit_price)}
                </div>
              )}
            </td>

            <td className={styles.money_cell}>
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <Icon size={12} icon='dollar' style={{ marginRight: 'auto' }} />
                {lineItem.unit_price && lineItem.quantity
                  ? formatMoney(lineItem.unit_price * lineItem.quantity)
                  : ''}
              </div>
            </td>

            {linkResourcesToLineItems && (
              <td style={{ verticalAlign: 'middle' }}>
                {getResourceMultiselect(lineItem, !editable)}
              </td>
            )}

            {
              <td
                style={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  maxWidth: 200,
                  textAlign: 'center',
                }}>
                {editable ? (
                  <InputGroup
                    type='text'
                    value={lineItem.vendor_site_url || ''}
                    readOnly={!editable}
                    onChange={(event) =>
                      updateItemValue(
                        lineItem,
                        'vendor_site_url',
                        event.target.value,
                      )
                    }
                  />
                ) : (
                  lineItem.vendor_site_url && (
                    <a
                      style={{ textAlign: 'center' }}
                      href={getClickableLink(lineItem.vendor_site_url)}
                      target='_blank'
                      rel='noreferrer'
                      onMouseDown={(e) => e.preventDefault()}>
                      <Icon icon={'share'} />
                    </a>
                  )
                )}
              </td>
            }

            {showRadioactiveCol && (
              <td style={{ textAlign: 'center' }}>
                {editable ? (
                  <Checkbox
                    defaultChecked={lineItem?.is_radioactive}
                    onChange={() =>
                      updateItemValue(
                        lineItem,
                        'is_radioactive',
                        !lineItem.is_radioactive,
                      )
                    }
                  />
                ) : (
                  <Icon
                    size='20'
                    icon={lineItem?.is_radioactive ? 'tick' : 'blank'}
                  />
                )}
              </td>
            )}

            {!editable && !isNew && (
              <td style={{ textAlign: 'center' }}>
                {!canLineBeMarkedReceived(lineItem) &&
                lineItem.date_received ? (
                  <Popover
                    minimal
                    content={getReceivedContent(lineItem)}
                    interactionKind={'hover'}>
                    <div
                      style={{
                        display: 'inline-flex',
                        gap: 2,
                        color: 'var(--status_received)',
                      }}>
                      <Icon icon={'tick'} size={20} />
                      Received
                    </div>
                  </Popover>
                ) : (
                  <StatusSelectButton
                    statusObject={getLineItemStatus(lineItem.status)}
                    readOnly
                    disabled
                  />
                  // getLineItemStatusLabel(lineItem.status)
                )}
              </td>
            )}

            {!editable &&
              !isNew &&
              lineItems.some((lineItem) =>
                canLineBeMarkedReceived(lineItem),
              ) && (
                <td style={{ textAlign: 'center' }}>
                  {canLineBeMarkedReceived(lineItem) && (
                    <Tooltip content={'Mark item received'}>
                      <Button
                        className={styles.received_button}
                        minimal
                        onClick={() => markLineItemReceived(lineItem)}
                        text={'Received?'}
                      />
                    </Tooltip>
                  )}
                </td>
              )}

            {editable && (
              <td>
                <div style={{ display: 'flex' }}>
                  {lineItem.status !== LINE_ITEM_STATUS.RECEIVED.key && (
                    <DeleteButton
                      miniVersion
                      useXIcon
                      onDeleteItem={() => onMarkItemForDelete(lineItem)}
                      tooltipText={'Remove Line Item'}
                    />
                  )}

                  <DuplicateButton
                    miniVersion
                    duplicateClickHandler={() => duplicateLineItem(lineItem)}
                    tooltipText={'Duplicate Line Item'}
                  />
                </div>
              </td>
            )}
          </tr>

          {(editable || (lineItem.comment && !compact)) && (
            <tr className={index % 2 ? styles.light_background : ''}>
              {showDummyCheckboxes && <td />}

              <td style={{ textAlign: 'end' }}>
                {editable && (
                  <span className={styles.comment_label}>Comment:</span>
                )}
              </td>

              <td
                colSpan={4}
                style={!editable ? { padding: '0 12px 12px' } : {}}>
                <div className={styles.comment_table_div}>
                  {editable ? (
                    <TextArea
                      fill
                      autoResize
                      value={lineItem.comment || ''}
                      onChange={(event) =>
                        updateItemValue(lineItem, 'comment', event.target.value)
                      }
                    />
                  ) : (
                    getCommentBox(0)
                  )}
                </div>
              </td>
              {<td colSpan={totalColumns - 5} />}
            </tr>
          )}
        </Fragment>
      );
    });

  let blankColsSpan = 5;
  let shippingColSpan = compact ? 4 : 3;
  let blankColsBeforeShippingSpeed = blankColsSpan - 1 - shippingColSpan;
  let showOtherShippingDetails = lineItems.some(
    (item) => item.status === LINE_ITEM_STATUS.PURCHASED.key,
  );

  let shippingRow = lineItems && lineItems.length > 0 && (
    <tr className={styles.shipping_row}>
      {showDummyCheckboxes && <td />}
      {blankColsBeforeShippingSpeed > 0 && (
        <td colSpan={blankColsBeforeShippingSpeed} />
      )}
      <td colSpan={shippingColSpan} style={{ verticalAlign: 'middle' }}>
        {(shippingItem?.id || editable) && (
          <div
            style={{
              borderColor: Colors.LIGHT_GRAY1,
              borderWidth: 'thin',
              borderStyle: 'solid',
            }}>
            {shippingContext.getShippingInfoGroup(
              editable,
              showOtherShippingDetails,
            )}
          </div>
        )}
      </td>
      <td className={styles.shipping_cell}>Shipping:</td>
      <td className={styles.shipping_cell}>
        {shippingContext.getShippingCostInput(editable)}
      </td>
      {linkResourcesToLineItems && <td />}
      <td />
      {showRadioactiveCol && <td />}
      {!editable && <td />}
      {!editable &&
        !isNew &&
        lineItems.filter((lineItem) => canLineBeMarkedReceived(lineItem))
          .length > 1 && (
          <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
            <Tooltip content={'Mark all items received'}>
              <Button
                className={styles.received_button}
                minimal
                onClick={() => markAllLineItemsReceived()}
                text={'All Received?'}
              />
            </Tooltip>
          </td>
        )}
    </tr>
  );

  let totalRow = lineItems && lineItems.length > 0 && (
    <tr>
      {showDummyCheckboxes && <td />}
      {blankColsBeforeShippingSpeed > 0 && (
        <td colSpan={blankColsBeforeShippingSpeed} />
      )}
      <td colSpan={shippingColSpan} />
      <td className={styles.order_total_label}>Order Total:</td>
      <td
        className={styles.table_total_cell}
        style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}>
          <Icon size={14} icon='dollar' style={{ marginRight: 'auto' }} />
          {formatMoney(grandTotal)}
        </div>
      </td>
    </tr>
  );

  const newButton = editable && (
    <AddNewButton
      clickHandler={CurrentLineItemData.getBlank}
      buttonText='Add Line Item'
    />
  );

  let shippingSpeedAmountUpdated =
    Object.hasOwn(shippingContext.updatedFields, 'shipping_speed') ||
    Object.hasOwn(shippingContext.updatedFields, 'amount') ||
    Object.hasOwn(shippingContext.updatedFields, 'tracking_number') ||
    Object.hasOwn(shippingContext.updatedFields, 'expected_delivery_date');

  const undoButton = editable &&
    !isNew &&
    (lineItems?.length > 0 || deletedLineItems?.length > 0) &&
    (linesHaveBeenUpdated || shippingSpeedAmountUpdated) && (
      <UndoButton clickHandler={undoChanges} />
    );

  const saveButton = editable &&
    lineItems?.length > 0 &&
    (linesHaveBeenUpdated || shippingSpeedAmountUpdated) && (
      <SaveButton disabled={disableSave} clickHandler={saveClicked} />
    );

  const footer = (
    <ButtonFooter marginTop={0}>
      {undoButton}
      {newButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    <>
      <table
        className={`${
          compact ? styles.line_items_table_compact : styles.line_items_table
        } ${editable ? styles.line_items_table_edit_mode : ''}`}>
        <thead>{headers}</thead>
        <tbody>{lineItemContent}</tbody>
        <tfoot>
          {shippingRow}
          {totalRow}
        </tfoot>
      </table>
      {footer}
    </>
  );
};

LineItemList.propTypes = {
  editable: PropTypes.bool,
  request: PropTypes.shape({
    id: PropTypes.number,
    requested_delivery_address_id: PropTypes.number,
    shipping_speed: PropTypes.string,
    line_items: PropTypes.array,
    parent_organization_id: oneOfType([PropTypes.string, PropTypes.number]),
  }),
  transaction: PropTypes.shape({
    id: PropTypes.number,
    line_items: PropTypes.array,
    shippings: PropTypes.array,
    request: PropTypes.object,
    parent_organization_id: oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onUpdateLineItems: PropTypes.func,
  isNew: PropTypes.bool,
  compact: PropTypes.bool,
  setEditState: PropTypes.func,
};

export default LineItemList;
